import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Input } from 'Components/atoms/Input';
import { Form } from 'Components/molecules/Form';
import { Link } from 'Components/molecules/Link';

import { useRegPlateForm } from './useRegPlateForm';

import './_style.scss';

/**
 * @name RegPlateForm
 * @param {string} action
 * @param {string} cssPrefix
 * @param {node|string} ctaLabel
 * @param {string} ctaUrl
 * @param {Object} extraClassNames
 * @param {string} inputId
 * @param {string} inputLabel
 * @param {boolean} isFromArticle - Adds the VRM to the URL as search query (`?reg-plate=${regPlate}`). Default: false.
 * @param {string} inputPlaceholder
 * @param {boolean} submitTypeIsButton
 * @param {function} submitAction
 * @param {string} vrm
 * @param trackingData
 * @param isRequired
 * @returns {JSX.Element}
 * @constructor
 */
const RegPlateForm = ({
  action,
  context: {
    cssPrefix,
  },
  ctaLabel,
  ctaUrl,
  extraClassNames,
  inputId,
  inputLabel,
  inputPlaceholder,
  isRequired,
  passParams,
  queryType,
  submitAction, // One data parameter - the valuation result
  submitTypeIsButton,
  trackingData,
  vrm, // if passed vrm number from previous steps
}) => {
  const vrmToUse = vrm || '';

  const {
    formData,
    handleInputChange,
    handleSubmit,
    error,
  } = useRegPlateForm({
    ctaUrl,
    isRequired,
    passParams,
    queryType,
    submitAction,
    submitTypeIsButton,
    trackingData,
    vrmToUse,
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.clear();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const { vehicleReg } = formData;
  const vrmField = vrm || vehicleReg;

  return (
    <Form
      action={action}
      extraClassNames={{
        [`${cssPrefix}__regPlateForm`]: true,
        ...extraClassNames,
      }}
      method="get"
      onSubmit={handleSubmit}
      target="_blank"
    >
      <div className={`${cssPrefix}__regPlateForm--actions`}>
        {inputLabel && (
          <div className={`${cssPrefix}__regPlateForm--input-label`}>{inputLabel}</div>
        )}
        <Input
          extraClassNames={{
            [`${cssPrefix}__regPlateForm--input`]: true,
          }}
          id={inputId}
          name={inputId}
          onChange={handleInputChange}
          placeholder={inputPlaceholder}
          type="text"
          value={vrmField}
        />
        {submitTypeIsButton ? (
          <Button
            extraClassNames={{
              [`${cssPrefix}__regPlateForm--button`]: true,
            }}
            label={ctaLabel}
            type="submit"
          />
        ) : (
          <Link
            extraClassNames={{
              [`${cssPrefix}__regPlateForm--link`]: true,
            }}
            href={ctaUrl}
            onClick={handleSubmit}
            rel="sponsored"
          >
            {ctaLabel}
          </Link>
        )}
      </div>
      {error && (
        <p
          className={classNames({
            [`${cssPrefix}__regPlateForm--input-error`]: true,
            '-is-visible': error !== '',
          })}
        >
          {error}
        </p>
      )}
    </Form>

  );
};

RegPlateForm.propTypes = {
  action: PropTypes.string,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  ctaLabel: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]),
  ctaUrl: PropTypes.string,
  extraClassNames: PropTypes.shape(),
  inputId: PropTypes.string,
  inputLabel: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  isRequired: PropTypes.bool,
  passParams: PropTypes.bool,
  queryType: PropTypes.string,
  submitAction: PropTypes.func,
  submitTypeIsButton: PropTypes.bool,
  trackingData: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.shape,
  ]),
  vrm: PropTypes.string,
};

RegPlateForm.defaultProps = {
  action: '',
  context: {},
  ctaLabel: '',
  ctaUrl: '',
  extraClassNames: {},
  inputId: 'vehicleReg',
  inputLabel: '',
  inputPlaceholder: '',
  isRequired: true,
  passParams: false,
  queryType: 'ukv',
  submitAction: () => {},
  submitTypeIsButton: false,
  trackingData: {},
  vrm: null,
};

export default withPolaris(RegPlateForm);
